/**
 * Utilidad para búsqueda inteligente y ordenamiento por relevancia de estudios.
 * Permite buscar en código, nombre y sinónimos, priorizando las coincidencias
 * más exactas y directas en la parte superior de la lista.
 */

/**
 * Limpia y normaliza una cadena (remueve acentos, convierte a minúsculas y elimina espacios extras).
 * @param {any} str
 * @returns {string}
 */
export const cleanSearchText = (str) =>
  `${str || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * Extrae una lista plana de sinónimos limpios desde cualquier formato
 * (string separado por comas, array de strings, array de objetos o un solo objeto).
 * @param {any} sinonimos
 * @returns {string[]}
 */
export const extractSinonimosList = (sinonimos) => {
  if (!sinonimos) return [];

  if (Array.isArray(sinonimos)) {
    return sinonimos
      .map((s) => {
        if (typeof s === "string") return cleanSearchText(s);
        if (s && typeof s === "object") {
          return cleanSearchText(s.sinonimo || s.nombre || "");
        }
        return "";
      })
      .filter(Boolean);
  }

  if (typeof sinonimos === "string") {
    return sinonimos
      .split(",")
      .map((s) => cleanSearchText(s))
      .filter(Boolean);
  }

  if (typeof sinonimos === "object") {
    const val = sinonimos.sinonimo || sinonimos.nombre || "";
    return val ? [cleanSearchText(val)] : [];
  }

  return [];
};

/**
 * Filtra y ordena una lista de opciones de estudios según su relevancia respecto al texto de entrada.
 * @param {Array<Object>} options Lista de estudios disponibles.
 * @param {string} inputValue Texto ingresado por el usuario en el buscador.
 * @returns {Array<Object>} Lista filtrada y ordenada descendentemente por relevancia.
 */
export const filterAndRankEstudios = (options, stateOrInput) => {
  if (!Array.isArray(options)) return [];

  const rawText =
    typeof stateOrInput === "string"
      ? stateOrInput
      : stateOrInput?.inputValue ?? "";

  const input = cleanSearchText(rawText);
  if (!input) return options;

  // Separar en palabras clave para soportar búsquedas de varias palabras (ej. "quimica 6", "biometria hematica")
  const tokens = input.split(/\s+/).filter(Boolean);

  const scored = [];

  for (let i = 0; i < options.length; i++) {
    const opt = options[i];
    if (!opt) continue;

    const nom = cleanSearchText(opt.nombre);
    const cod = cleanSearchText(opt.codigo);
    const sinList = extractSinonimosList(opt.sinonimos);
    const sinConcat = sinList.join(" ");
    const combined = `${nom} ${cod} ${sinConcat}`;

    let score = 0;

    // 1. Coincidencia EXACTA
    if (cod === input) {
      score += 1000;
    } else if (nom === input) {
      score += 900;
    } else if (sinList.includes(input)) {
      score += 800;
    }

    // 2. Comienza con el texto buscado
    if (cod.startsWith(input)) {
      score += 700;
    }
    if (nom.startsWith(input)) {
      score += 600;
    }
    if (sinList.some((s) => s.startsWith(input))) {
      score += 500;
    }

    // 3. Coincidencia multi-token (todas las palabras escritas están presentes)
    if (tokens.length > 1) {
      const allTokensMatch = tokens.every((tok) => combined.includes(tok));
      if (allTokensMatch) {
        score += 400;
        // Puntos extra si los tokens coinciden específicamente en el nombre o código
        if (tokens.every((tok) => nom.includes(tok))) score += 70;
        if (tokens.every((tok) => cod.includes(tok))) score += 80;
      }
    }

    // 4. Coincidencia al inicio de alguna palabra individual (Word boundary)
    const nameWords = nom.split(/\s+/);
    if (nameWords.some((w) => w.startsWith(input))) {
      score += 200;
    }
    const codeWords = cod.split(/\s+/);
    if (codeWords.some((w) => w.startsWith(input))) {
      score += 250;
    }
    if (sinList.some((s) => s.split(/\s+/).some((w) => w.startsWith(input)))) {
      score += 150;
    }

    // 5. Coincidencia por subcadena (contiene el texto en cualquier posición)
    if (cod.includes(input)) {
      score += 100;
    }
    if (nom.includes(input)) {
      score += 80;
    }
    if (sinList.some((s) => s.includes(input))) {
      score += 40;
    }

    // Si obtuvo puntuación, califica como coincidencia válida
    if (score > 0) {
      scored.push({ opt, score });
    }
  }

  // Ordenar por score descendente (estable)
  scored.sort((a, b) => b.score - a.score);

  const result = scored.map((item) => item.opt);
  console.log(
    `--> [BUSCADOR INTELIGENTE] Buscado: "${input}" | Coincidencias: ${result.length}`,
    result.map((r) => `[${r.codigo}] ${r.nombre}`)
  );

  return result;
};

export default filterAndRankEstudios;
